import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { FloppyDisk, Trash, ArrowLeft, Warning, Lock, ChatText, Sparkle, Printer } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/auth/AuthContext';
import { knowledgeApi } from './knowledgeApi';
import RichEditor from './RichEditor';
import CommentsPanel from './CommentsPanel';
import AiAssistantPanel from './AiAssistantPanel';
import LinkedDocsPanel from '@/pages/docs/LinkedDocsPanel';
import { formatTime } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PEER_COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
function colorFor(id) {
  let h = 0; const s = String(id || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PEER_COLORS[h % PEER_COLORS.length];
}

export default function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { reloadTree, meta, tree } = useOutletContext();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [articleUid, setArticleUid] = useState(null);
  const [html, setHtml] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savedAt, setSavedAt] = useState(null);
  const autoRef = useRef(null);
  const editorRef = useRef(null);
  const [vis, setVis] = useState({ mode: 'inherit', dept_ids: [], user_ids: [] });
  const [audience, setAudience] = useState({ departments: [], users: [] });
  // 共同編集
  const [collab, setCollab] = useState(null);
  const [peers, setPeers] = useState([]);
  // コメント
  const [comments, setComments] = useState([]);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [activeComment, setActiveComment] = useState(null);
  // Batch 9 · iter_162 — AI writing assistant panel. `enabled` is
  // driven by /api/ai/prefs; we still show the toolbar button when
  // disabled so users can discover the settings, but the panel body
  // shows a disabled state instead of the form.
  const [aiOpen, setAiOpen] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [aiSelection, setAiSelection] = useState('');
  useEffect(() => {
    axios.get(`${API}/ai/prefs`).then((r) => setAiEnabled(r.data?.enabled !== false)).catch(() => setAiEnabled(true));
  }, []);
  // Poll the editor's current selection while the panel is open so
  // 書き直し / 翻訳 pick up the selection without a manual refresh.
  useEffect(() => {
    if (!aiOpen) return undefined;
    const tick = () => {
      const ed = editorRef.current;
      if (!ed) return;
      const { from, to } = ed.state.selection;
      if (from === to) { setAiSelection(''); return; }
      const t = ed.state.doc.textBetween(from, to, '\n', '\n');
      setAiSelection(t);
    };
    tick();
    const h = setInterval(tick, 350);
    return () => clearInterval(h);
  }, [aiOpen]);
  const aiInsertAtCaret = (text) => {
    const ed = editorRef.current;
    if (!ed || !text) return;
    ed.chain().focus().insertContent(text).run();
    toast.success('本文に挿入しました');
  };
  const aiReplaceSelection = (text) => {
    const ed = editorRef.current;
    if (!ed || !text) return;
    if (aiSelection) ed.chain().focus().insertContent(text).run();
    else ed.chain().focus().insertContent(text).run();
    toast.success('本文を更新しました');
  };
  const aiAppendToEnd = (text) => {
    const ed = editorRef.current;
    if (!ed || !text) return;
    ed.chain().focus().command(({ tr, state }) => {
      tr.insertText('\n' + text, state.doc.content.size);
      return true;
    }).run();
    toast.success('末尾に追加しました');
  };

  useEffect(() => { knowledgeApi.audienceOptions().then(setAudience).catch(() => {}); }, []);

  const setVisMode = (mode) => { setVis((v) => ({ ...v, mode })); setDirty(true); };
  const toggleDept = (did) => { setVis((v) => ({ ...v, dept_ids: v.dept_ids.includes(did) ? v.dept_ids.filter((x) => x !== did) : [...v.dept_ids, did] })); setDirty(true); };
  const toggleUser = (uid) => { setVis((v) => ({ ...v, user_ids: v.user_ids.includes(uid) ? v.user_ids.filter((x) => x !== uid) : [...v.user_ids, uid] })); setDirty(true); };

  useEffect(() => {
    let active = true;
    setLoading(true); setDirty(false);
    knowledgeApi.article(id).then((a) => {
      if (!active) return;
      setArticleUid(a.id);
      setForm({ title: a.title || '', icon: a.icon || '', category: a.category || '', tags: (a.tags || []).join(', '), status: a.status || 'draft' });
      setHtml(a.content_html || '');
      setVis(a.visibility && a.visibility.mode ? { mode: a.visibility.mode, dept_ids: a.visibility.dept_ids || [], user_ids: a.visibility.user_ids || [] } : { mode: 'inherit', dept_ids: [], user_ids: [] });
      // URL が UUID または旧 slug の場合、正式な slug へ静かに置換
      if (a.slug && id !== a.slug) {
        navigate(`/knowledge/p/${a.slug}/edit`, { replace: true });
      }
    }).catch(() => { if (active) toast.error('ページを読み込めません'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, navigate]);

  // 共同編集セッション(Yjs + WebSocket)
  useEffect(() => {
    if (!user) return undefined;
    const ydoc = new Y.Doc();
    const wsBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http/, 'ws') + '/api/knowledge/collab';
    const provider = new WebsocketProvider(wsBase, id, ydoc, { connect: true });
    const color = colorFor(user.id);
    provider.awareness.setLocalStateField('user', { name: user.name || user.email, color });
    const onAw = () => {
      const seen = [];
      provider.awareness.getStates().forEach((s) => { if (s.user) seen.push(s.user); });
      setPeers(seen);
    };
    provider.awareness.on('change', onAw); onAw();
    setCollab({ ydoc, provider, color });
    return () => {
      provider.awareness.off('change', onAw);
      try { provider.destroy(); } catch { /* noop */ }
      try { ydoc.destroy(); } catch { /* noop */ }
      setCollab(null); setPeers([]);
    };
  }, [id, user]);

  const loadComments = useCallback(async () => {
    try { setComments(await knowledgeApi.listComments(id)); } catch { /* noop */ }
  }, [id]);
  useEffect(() => { loadComments(); const t = setInterval(loadComments, 4000); return () => clearInterval(t); }, [loadComments]);

  const createComment = async (commentId, quote) => {
    const text = window.prompt('コメントを入力してください');
    if (!text || !text.trim()) return false;
    try {
      await knowledgeApi.addComment(id, { comment_id: commentId, text: text.trim(), quote });
      await loadComments();
      setCommentsOpen(true); setActiveComment(commentId);
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || 'コメントを追加できませんでした'); return false; }
  };
  const replyTo = async (c, text) => { try { await knowledgeApi.replyComment(c.id, text); await loadComments(); } catch (e) { toast.error(e?.response?.data?.detail || '返信できませんでした'); } };
  const resolveThread = async (c) => { try { const u = await knowledgeApi.resolveComment(c.id); if (u.resolved) editorRef.current?.commands?.unsetCommentMark?.(c.comment_id); await loadComments(); } catch { /* noop */ } };
  const deleteThread = async (c) => { try { await knowledgeApi.deleteComment(c.id); editorRef.current?.commands?.unsetCommentMark?.(c.comment_id); await loadComments(); } catch (e) { toast.error(e?.response?.data?.detail || '削除できませんでした'); } };

  // 未保存ガード（再読込・タブ閉じ）
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const set = (k) => (e) => { setForm((p) => ({ ...p, [k]: e.target.value })); setDirty(true); };

  const save = async (silent = false) => {
    setSaving(true);
    try {
      await knowledgeApi.update(id, {
        title: form.title, icon: form.icon, category: form.category,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        status: form.status, content_html: html, visibility: vis,
      });
      setDirty(false);
      setSavedAt(new Date());
      await reloadTree();
      if (!silent) toast.success('保存しました');
    } catch (e) { if (!silent) toast.error(e?.response?.data?.detail || 'エラーが発生しました'); }
    finally { setSaving(false); }
  };

  // オートセーブ（変更の1.5秒後に自動保存）
  useEffect(() => {
    if (loading || !dirty) return undefined;
    if (autoRef.current) clearTimeout(autoRef.current);
    autoRef.current = setTimeout(() => { save(true); }, 1500);
    return () => { if (autoRef.current) clearTimeout(autoRef.current); };
  }, [dirty, form, html]); // eslint-disable-line react-hooks/exhaustive-deps

  const doDelete = async () => {
    try {
      await knowledgeApi.remove(id);
      setConfirmDel(false); setDirty(false);
      await reloadTree();
      toast.success('ページを削除しました');
      navigate('/knowledge');
    } catch (e) { toast.error(e?.response?.data?.detail || 'エラーが発生しました'); }
  };

  if (loading || !form) return <div className="p-8 text-sm text-zinc-400">読み込み中…</div>;
  if (!meta?.can_edit) return <div className="p-8 text-sm text-rose-500" data-testid="kb-no-permission">編集権限がありません</div>;

  const openCount = comments.filter((c) => !c.resolved).length;

  return (
    <div className={`knowledge-print-document mx-auto max-w-4xl px-6 py-6 ${commentsOpen ? 'lg:pr-[26rem]' : ''}`} data-testid="kb-article-editor">
      <div className="kb-no-print mb-3 flex items-center justify-between gap-2">
        <button type="button" onClick={() => navigate(`/knowledge/p/${id}`)} data-testid="kb-back-btn"
          className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-800"><ArrowLeft size={15} />表示に戻る</button>
        <div className="flex items-center gap-2">
          {peers.length > 0 && (
            <div className="flex items-center -space-x-1.5 mr-1" data-testid="kb-presence-bar" title={`${peers.length}人が編集中`}>
              {peers.slice(0, 5).map((p, i) => (
                <span key={`${p.name || '?'}-${i}`} data-testid="kb-presence-avatar" style={{ backgroundColor: p.color || '#3b82f6' }}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white ring-2 ring-white">
                  {(p.name || '?').slice(0, 1)}
                </span>
              ))}
              {peers.length > 1 && <span className="pl-2.5 text-xs text-zinc-400">{peers.length}人が編集中</span>}
            </div>
          )}
          <button type="button" onClick={() => window.print()} data-testid="kb-print-btn-editor" title="印刷 / PDF出力"
            className="inline-flex items-center gap-1 rounded-md border border-zinc-200 px-2.5 py-1.5 text-sm text-zinc-600 hover:bg-zinc-50">
            <Printer size={15} />
          </button>
          <button type="button" onClick={() => setCommentsOpen((v) => !v)} data-testid="kb-comments-toggle"
            className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm ${commentsOpen ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}>
            <ChatText size={15} />{openCount > 0 && <span>{openCount}</span>}
          </button>
          <button type="button" onClick={() => setAiOpen((v) => !v)} data-testid="kb-ai-toggle"
            title="AIアシスタントを開く"
            className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm ${aiOpen ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}>
            <Sparkle size={15} weight={aiOpen ? 'fill' : 'regular'} />
            <span className="text-xs font-medium hidden sm:inline">AI</span>
          </button>
          {saving ? <span className="text-xs text-zinc-400" data-testid="kb-autosave">保存中…</span>
            : dirty ? <span className="text-xs text-amber-600" data-testid="kb-unsaved">未保存</span>
            : savedAt ? <span className="text-xs text-emerald-600" data-testid="kb-saved">保存済み {formatTime(savedAt)}</span>
            : null}
          <select value={form.status} onChange={set('status')} data-testid="kb-status-select"
            className="rounded-md border border-zinc-200 px-2 py-1.5 text-sm text-zinc-700 outline-none">
            <option value="draft">下書き</option>
            <option value="published">公開</option>
          </select>
          <button type="button" onClick={() => setConfirmDel(true)} data-testid="kb-delete-btn"
            className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50"><Trash size={15} /></button>
          <Button onClick={() => save(false)} disabled={saving} data-testid="kb-save-btn" className="bg-teal-700 hover:bg-teal-800">
            <FloppyDisk size={15} className="mr-1.5" />{saving ? '保存中…' : '保存'}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <input value={form.icon} onChange={set('icon')} data-testid="kb-icon-input" maxLength={4}
          placeholder="📄" className="w-12 shrink-0 rounded-md border border-zinc-200 px-2 py-2 text-center text-xl outline-none focus:border-blue-400" />
        <input value={form.title} onChange={set('title')} data-testid="kb-title-input"
          placeholder="ページタイトル" className="flex-1 min-w-0 border-0 border-b border-transparent px-1 py-2 text-3xl font-bold tracking-tight text-zinc-900 outline-none focus:border-zinc-200 placeholder:text-zinc-300" />
      </div>

      <div className="kb-no-print mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-zinc-400">カテゴリ</label>
          <input value={form.category} onChange={set('category')} data-testid="kb-category-input" list="kb-cat-list"
            placeholder="—" className="rounded-md border border-zinc-200 px-2 py-1 text-sm outline-none focus:border-blue-400 w-36" />
          <datalist id="kb-cat-list">{(meta.categories || []).map((c) => <option key={c} value={c} />)}</datalist>
        </div>
        <div className="flex items-center gap-1.5 flex-1 min-w-[180px]">
          <label className="text-xs text-zinc-400">タグ</label>
          <input value={form.tags} onChange={set('tags')} data-testid="kb-tags-input"
            placeholder="カンマ区切りで入力" className="flex-1 rounded-md border border-zinc-200 px-2 py-1 text-sm outline-none focus:border-blue-400" />
        </div>
      </div>

      <div className="kb-no-print mb-4 rounded-lg border border-zinc-200 p-3" data-testid="kb-visibility-section">
        <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-zinc-700"><Lock size={15} className="text-zinc-400" />公開範囲（閲覧できる人）</div>
        <select value={vis.mode} onChange={(e) => setVisMode(e.target.value)} data-testid="kb-vis-mode"
          className="rounded-md border border-zinc-200 px-2 py-1.5 text-sm text-zinc-700 outline-none focus:border-blue-400">
          <option value="inherit">親ページに従う（継承）</option>
          <option value="everyone">全員に公開</option>
          <option value="departments">指定した部門のみ</option>
          <option value="users">指定したユーザーのみ</option>
        </select>
        {vis.mode === 'inherit' && (
          <p className="mt-1.5 text-xs text-zinc-400" data-testid="kb-vis-inherit-hint">親ページの公開範囲を引き継ぎます（ルートページは全員に公開）。管理者と作成者は常に閲覧・編集できます。</p>
        )}
        {vis.mode === 'departments' && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="kb-vis-depts">
            {(audience.departments || []).map((d) => {
              const on = vis.dept_ids.includes(d.id);
              return (
                <button type="button" key={d.id} data-testid={`kb-vis-dept-${d.id}`} onClick={() => toggleDept(d.id)}
                  className={`rounded-full border px-2.5 py-1 text-xs ${on ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}>{d.name}</button>
              );
            })}
            {vis.dept_ids.length === 0 && <span className="text-xs text-amber-600">部門を1つ以上選択してください（未選択の場合、管理者と作成者のみ閲覧可）</span>}
          </div>
        )}
        {vis.mode === 'users' && (
          <div className="mt-2 max-h-44 overflow-y-auto rounded-md border border-zinc-200 divide-y divide-zinc-200" data-testid="kb-vis-users">
            {(audience.users || []).map((u) => (
              <label key={u.id} data-testid={`kb-vis-user-${u.id}`} className="flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-zinc-50">
                <input type="checkbox" checked={vis.user_ids.includes(u.id)} onChange={() => toggleUser(u.id)} className="accent-blue-600" />
                <span className="text-zinc-700">{u.name}</span>
                <span className="ml-auto text-xs text-zinc-400">{u.email}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {collab ? (
        <RichEditor key={id} ydoc={collab.ydoc} provider={collab.provider}
          user={{ name: user.name || user.email, color: collab.color }}
          initialHtml={html}
          tree={tree}
          onChange={(h) => { setHtml(h); setDirty(true); }}
          onEditorReady={(ed) => { editorRef.current = ed; }}
          onCreateComment={createComment}
          onSelectComment={(cid) => { setActiveComment(cid); setCommentsOpen(true); }} />
      ) : (
        <div className="py-8 text-sm text-zinc-400">共同編集セッションに接続中…</div>
      )}

      <LinkedDocsPanel targetType="kb_page" targetId={articleUid} className="mt-6 pt-4 border-t border-zinc-200" />

      <CommentsPanel
        comments={comments} open={commentsOpen} onClose={() => setCommentsOpen(false)}
        activeId={activeComment} currentUserId={user?.id} isAdmin={user?.role === 'admin'}
        onReply={replyTo} onResolve={resolveThread} onDelete={deleteThread} />

      <AiAssistantPanel
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        articleId={id}
        selectionText={aiSelection}
        enabled={aiEnabled}
        onInsert={(t) => (aiSelection ? aiReplaceSelection(t) : aiInsertAtCaret(t))}
        onAppend={aiAppendToEnd}
      />

      <Dialog open={confirmDel} onOpenChange={setConfirmDel}>
        <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="kb-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Warning size={18} weight="fill" className="text-rose-500" />ページを削除</DialogTitle>
            <DialogDescription>このページと、含まれるすべての子ページを削除します。元に戻せません。</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setConfirmDel(false)} data-testid="kb-delete-cancel">キャンセル</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="kb-delete-confirm">削除</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
